import React, { useMemo, useState } from 'react'
import { Box, Checkbox, FormControlLabel, IconButton, MenuItem, Paper, TextField, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { useModal } from '../../../../../store/ModalProvider'
import CustomButton from '../../CustomButton/CustomButton'
import { AnimatedDataGridPro } from '../../CustomDataGrid'
import { dataGridDensityTransitionSx } from '../../CustomDataGrid/dataGridDensityTransitionSx'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { popupInputLabelSx } from '../popupInputStyles'
import type { SameChapaModalData } from '../../../../../types/modal'

type ServiceRow = {
	id: string
	dataInicio: string
	horaInicio: string
	servico: string
	localInicio: string
	localTermino: string
	horaTermino: string
	dataTermino: string
	cliente: string
	observacoes: string
	motorista: string
}

const tableColumns = [
	{ key: 'dataInicio', label: 'Data Início', width: '110px' },
	{ key: 'horaInicio', label: 'Hora Início', width: '90px' },
	{ key: 'servico', label: 'Serviço', width: '90px' },
	{ key: 'localInicio', label: 'Local Início', width: '120px' },
	{ key: 'localTermino', label: 'Local Término', width: '120px' },
	{ key: 'horaTermino', label: 'Hora Término', width: '90px' },
	{ key: 'dataTermino', label: 'Data Término', width: '110px' },
	{ key: 'cliente', label: 'Cliente', width: '130px' },
	{ key: 'observacoes', label: 'Observações', width: '130px' },
	{ key: 'motorista', label: 'Motorista', width: '130px' },
]

const defaultRows: ServiceRow[] = [
	{
		id: 'row-1',
		dataInicio: '02/05/2024',
		horaInicio: '10:00',
		servico: '0090000',
		localInicio: 'Mafra',
		localTermino: 'Ericeira',
		horaTermino: '11:00',
		dataTermino: '02/05/2024',
		cliente: 'Travelite, Unipe...',
		observacoes: 'Lorem Ipsum dol...',
		motorista: 'Agostinho',
	},
	{
		id: 'row-2',
		dataInicio: '02/05/2024',
		horaInicio: '11:00',
		servico: '0090000',
		localInicio: 'Ericeira',
		localTermino: 'Mafra',
		horaTermino: '12:00',
		dataTermino: '02/05/2024',
		cliente: 'Travelite, Unipe...',
		observacoes: 'Lorem Ipsum dol...',
		motorista: 'Agostinho',
	},
	{
		id: 'row-3',
		dataInicio: '02/05/2024',
		horaInicio: '12:00',
		servico: '0090000',
		localInicio: 'Mafra',
		localTermino: 'Ericeira',
		horaTermino: '13:00',
		dataTermino: '02/05/2024',
		cliente: 'Travelite, Unipe...',
		observacoes: 'Lorem Ipsum dol...',
		motorista: 'Agostinho',
	},
	{
		id: 'row-4',
		dataInicio: '02/05/2024',
		horaInicio: '14:00',
		servico: '0090000',
		localInicio: 'Ericeira',
		localTermino: 'Mafra',
		horaTermino: '15:00',
		dataTermino: '02/05/2024',
		cliente: 'Travelite, Unipe...',
		observacoes: 'Lorem Ipsum dol...',
		motorista: 'Agostinho',
	},
	{
		id: 'row-5',
		dataInicio: '02/05/2024',
		horaInicio: '15:00',
		servico: '0090000',
		localInicio: 'Mafra',
		localTermino: 'Ericeira',
		horaTermino: '16:00',
		dataTermino: '02/05/2024',
		cliente: 'Travelite, Unipe...',
		observacoes: 'Lorem Ipsum dol...',
		motorista: 'Agostinho',
	},
]

const formFieldSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#F5F7FC',
		borderRadius: '4px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0)',
		},
	},
	'& .MuiSelect-select': {
		padding: '8px 36px 8px 12px',
		lineHeight: 1.3,
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '10px',
	},
}

const outlinedFieldSx = {
	...formFieldSx,
	'& .MuiOutlinedInput-root': {
		...formFieldSx['& .MuiOutlinedInput-root'],
		backgroundColor: '#FFFFFF',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
	},
}

const checkboxLabelSx = {
	fontSize: '13px',
	fontWeight: 500,
	color: '#092056',
	lineHeight: 1.3,
}
const checkboxSx = {
	color: 'rgba(134, 149, 175, 0.5)',
	'&.Mui-checked': {
		color: '#00A5FF',
	},
}
const tightCheckboxSx = {
	...checkboxSx,
	padding: '2px',
}
const tightFormControlLabelSx = {
	margin: 0,
	alignItems: 'center',
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '11px',
		color: '#8695AF',
		whiteSpace: 'nowrap',
		letterSpacing: '0.17px',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		fontWeight: 400,
		color: '#092056',
		borderBottom: 'none',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	'& .MuiDataGrid-row': {
		backgroundColor: '#FFFFFF',
		borderBottom: 'none',
	},
	'& .MuiDataGrid-row.Mui-selected': {
		backgroundColor: 'rgba(0, 165, 255, 0.08)',
	},
	'& .MuiDataGrid-row.Mui-selected:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.12)',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const gridColumns: GridColDef<ServiceRow>[] = tableColumns.map(column => ({
	field: column.key,
	headerName: column.label,
	width: Number.parseInt(column.width, 10),
}))

const CustomModalSameChapaServices: React.FC = () => {
	const { closeModal, modalData } = useModal()
	const sameChapaData = modalData as SameChapaModalData | undefined

	const rows = useMemo(() => {
		if (sameChapaData?.services?.length) {
			return sameChapaData.services
		}
		return defaultRows
	}, [sameChapaData])
	const [selectedRowIds, setSelectedRowIds] = useState<string[]>(() => rows.map(row => row.id))
	const [actionsOpen, setActionsOpen] = useState(false)
	const [rowDensity, setRowDensity] = useState<'compact' | 'expanded'>('compact')
	const density = rowDensity === 'compact' ? 'compact' : 'standard'
	const rowHeight = rowDensity === 'compact' ? 32 : 44
	const bodyHeight = Math.min(240, rows.length * rowHeight)
	const gridHeight = Math.max(bodyHeight + 40, 160)

	if (!sameChapaData) {
		return null
	}

	const warningMessage = sameChapaData.warningMessage

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				pointerEvents: 'auto',
				width: '100%',
				height: '100%',
				padding: { xs: '16px', md: '32px' },
			}}
		>
			<Paper
				elevation={0}
				sx={{
					width: 'min(1200px, 100%)',
					height: { xs: '100%', md: 'auto' },
					maxHeight: { xs: '100%', md: 'none' },
					borderRadius: { xs: '3px', md: '3px' },
					backgroundColor: '#FFFFFF',
					border: '1px solid rgba(134, 149, 175, 0.2)',
					boxShadow: '0 24px 60px rgba(9, 32, 86, 0.16)',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						padding: '16px 24px',
					}}
				>
					<Typography
						sx={{
							fontSize: '16px',
							fontWeight: 600,
							fontFamily: 'Poppins, sans-serif',
							color: '#092056',
							lineHeight: 1.75,
							letterSpacing: '0.15px',
						}}
					>
						Escalar outros serviços da mesma chapa / requisição
					</Typography>
				</Box>

				<Box
					sx={{
						flex: 1,
						padding: '8px 24px 24px',
						display: 'flex',
						flexDirection: 'column',
						gap: '24px',
						overflowY: { xs: 'auto', md: 'visible' },
					}}
				>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
						<Typography
							sx={{
								fontSize: '24px',
								fontWeight: 600,
								fontFamily: 'Poppins, sans-serif',
								color: '#092056',
								lineHeight: 1.334,
							}}
						>
							Chapa, Viatura e Motorista
						</Typography>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
							<TextField
								label='Chapa/Requisição'
								size='small'
								value={sameChapaData.chapa}
								fullWidth
								sx={{ ...formFieldSx, flex: '1 1 0' }}
								InputProps={{ readOnly: true }}
							/>
							<TextField
								label='Motorista'
								size='small'
								select
								value={sameChapaData.motorista ?? ''}
								fullWidth
								sx={{ ...outlinedFieldSx, flex: '1 1 0', maxWidth: '276px' }}
								SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
							>
								<MenuItem value={sameChapaData.motorista ?? ''}>
									{sameChapaData.motorista ?? '0109 - Agostinho Ferreira'}
								</MenuItem>
							</TextField>
							<TextField
								size='small'
								select
								value={sameChapaData.secondMotorista ?? '2º Motorista'}
								fullWidth
								sx={{ ...outlinedFieldSx, flex: '1 1 0', maxWidth: '276px' }}
								SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
							>
								<MenuItem value='2º Motorista'>2º Motorista</MenuItem>
							</TextField>
							<TextField
								label='Viatura'
								size='small'
								select
								value={sameChapaData.viatura ?? ''}
								fullWidth
								sx={{ ...outlinedFieldSx, flex: '1 1 0', maxWidth: '276px' }}
								SelectProps={{ IconComponent: KeyboardArrowDownIcon }}
							>
								<MenuItem value={sameChapaData.viatura ?? ''}>
									{sameChapaData.viatura ?? '0012 - Iveco 5T - I. Mota'}
								</MenuItem>
							</TextField>
						</Box>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
								<FormControlLabel
									sx={tightFormControlLabelSx}
									control={
										<Checkbox
											size='small'
											sx={tightCheckboxSx}
										/>
									}
									label={<Typography sx={checkboxLabelSx}>Escalar Viatura</Typography>}
								/>
								<FormControlLabel
									sx={tightFormControlLabelSx}
									control={
										<Checkbox
											size='small'
											defaultChecked
											sx={tightCheckboxSx}
										/>
									}
									label={<Typography sx={checkboxLabelSx}>Escalar Motorista</Typography>}
								/>
							</Box>
							<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
								<FormControlLabel
									sx={tightFormControlLabelSx}
									control={
										<Checkbox
											size='small'
											sx={tightCheckboxSx}
										/>
									}
									label={
										<Typography sx={checkboxLabelSx}>
											Se escalar viatura c/ condição impeditiva, retira a viatura que já esteja escalada
										</Typography>
									}
								/>
								<FormControlLabel
									sx={tightFormControlLabelSx}
									control={
										<Checkbox
											size='small'
											defaultChecked
											sx={tightCheckboxSx}
										/>
									}
									label={
										<Typography sx={checkboxLabelSx}>
											Se não conseguir escalar o 1º motorista, também não escala o 2º
										</Typography>
									}
								/>
							</Box>
						</Box>
					</Box>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
								gap: '16px',
							}}
						>
							<Typography
								sx={{
									fontSize: '20px',
									fontWeight: 600,
									fontFamily: 'Poppins, sans-serif',
									color: '#092056',
									lineHeight: 1.6,
								}}
							>
								{rows.length} Serviços na mesma Chapa
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
								<TextField
									label='Ações'
									size='small'
									select
									value='Descalar em Bloco'
									sx={{
										...outlinedFieldSx,
										width: '190px',
										'& .MuiInputLabel-root': {
											...outlinedFieldSx['& .MuiInputLabel-root'],
											color: actionsOpen ? '#00A5FF' : '#092056',
										},
										'& .MuiOutlinedInput-root': {
											...outlinedFieldSx['& .MuiOutlinedInput-root'],
											'& fieldset': {
												borderColor: actionsOpen ? '#00A5FF' : 'rgba(134, 149, 175, 0.3)',
											},
										},
									}}
									SelectProps={{
										onOpen: () => setActionsOpen(true),
										onClose: () => setActionsOpen(false),
										IconComponent: KeyboardArrowDownIcon,
										MenuProps: {
											PaperProps: {
												sx: {
													borderRadius: '4px',
													boxShadow:
														'0 3px 14px 2px rgba(9, 32, 86, 0.12), 0 8px 10px 1px rgba(9, 32, 86, 0.14), 0 5px 5px -3px rgba(9, 32, 86, 0.2)',
													'& .MuiMenuItem-root': {
														fontSize: '12px',
														fontFamily: 'Poppins, sans-serif',
														fontWeight: 500,
														color: '#092056',
														lineHeight: 1.5,
													},
												},
											},
										},
									}}
								>
									<MenuItem value='Descalar em Bloco'>Descalar em Bloco</MenuItem>
								</TextField>
								{actionsOpen ? (
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: '12px',
											padding: '8px 16px',
											borderRadius: '4px',
											background: 'linear-gradient(90deg, #00A5FF 0%, #37CDFF 100%)',
											color: '#FFFFFF',
										}}
									>
										<InfoOutlinedIcon sx={{ fontSize: 22 }} />
										<Typography
											sx={{
												fontSize: '14px',
												fontWeight: 500,
												fontFamily: 'Poppins, sans-serif',
												color: '#FFFFFF',
												lineHeight: 1.5,
												letterSpacing: '0.15px',
												whiteSpace: 'nowrap',
											}}
										>
											Guarde alterações antes de selecionar a Ação
										</Typography>
									</Box>
								) : null}
								<Box sx={{ display: 'flex', gap: '6px' }}>
									<IconButton
										size='small'
										sx={{
											borderRadius: '6px',
											border: '1px solid rgba(134, 149, 175, 0.35)',
											color: '#00A5FF',
											backgroundColor: rowDensity === 'compact' ? '#F5F7FC' : '#FFFFFF',
										}}
										aria-pressed={rowDensity === 'compact'}
										onClick={() => setRowDensity('compact')}
									>
										<TableRowsIcon fontSize='inherit' />
									</IconButton>
									<IconButton
										size='small'
										sx={{
											borderRadius: '6px',
											border: '1px solid rgba(134, 149, 175, 0.35)',
											color: '#00A5FF',
											backgroundColor: rowDensity === 'expanded' ? '#F5F7FC' : '#FFFFFF',
										}}
										aria-pressed={rowDensity === 'expanded'}
										onClick={() => setRowDensity('expanded')}
									>
										<ViewHeadlineIcon fontSize='inherit' />
									</IconButton>
								</Box>
							</Box>
						</Box>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										sx={checkboxSx}
									/>
								}
								label={<Typography sx={checkboxLabelSx}>Descalar Motorista</Typography>}
							/>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										sx={checkboxSx}
									/>
								}
								label={<Typography sx={checkboxLabelSx}>Descalar 2º Motorista</Typography>}
							/>
							<FormControlLabel
								control={
									<Checkbox
										size='small'
										sx={checkboxSx}
									/>
								}
								label={<Typography sx={checkboxLabelSx}>Descalar Viatura</Typography>}
							/>
						</Box>

						{warningMessage ? (
							<Box
								sx={{
									display: 'flex',
									alignItems: 'flex-start',
									padding: '0 16px',
									borderRadius: '4px',
									background: 'linear-gradient(104.92deg, #FF5C00 0.42425%, #EDBF67 99.576%)',
								}}
							>
								<Box sx={{ display: 'flex', padding: '7px 12px 7px 0' }}>
									<WarningAmberOutlinedIcon sx={{ color: '#FFFFFF', fontSize: 22 }} />
								</Box>
								<Box sx={{ display: 'flex', padding: '8px 0' }}>
									<Typography
										sx={{
											fontSize: '14px',
											fontWeight: 500,
											fontFamily: 'Poppins, sans-serif',
											color: '#FFFFFF',
											lineHeight: 1.5,
											letterSpacing: '0.15px',
										}}
									>
										{warningMessage}
									</Typography>
								</Box>
							</Box>
						) : null}
						<Box
							sx={{
								borderRadius: '12px',
								backgroundColor: 'transparent',
								border: 'none',
								overflow: 'hidden',
							}}
						>
							<Box sx={{ height: gridHeight }}>
								<AnimatedDataGridPro
									slots={{ columnMenu: CustomColumnMenu }}
									slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
									rows={rows}
									columns={gridColumns}
									density={density}
									rowHeight={rowHeight}
									columnHeaderHeight={40}
									checkboxSelection
									rowSelectionModel={selectedRowIds}
									onRowSelectionModelChange={newSelection => {
										setSelectedRowIds(newSelection as string[])
									}}
									disableRowSelectionOnClick
									hideFooter
									sx={[tableSx, dataGridDensityTransitionSx]}
								/>
							</Box>
						</Box>
					</Box>
				</Box>

				<Box
					sx={{
						padding: '8px 16px',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '12px',
						backgroundColor: '#FFFFFF',
					}}
				>
					<CustomButton
						variant='text'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					<CustomButton
						variant='contained'
						color='primary'
						sx={popupActionButtonSx}
					>
						Guardar
					</CustomButton>
				</Box>
			</Paper>
		</Box>
	)
}

export default CustomModalSameChapaServices
